import { CommonModule } from '@angular/common';
import { Component, OnInit, SimpleChanges, viewChild } from '@angular/core';
import { MatChipsModule } from '@angular/material/chips';
import { MatOption, MatOptionModule } from '@angular/material/core';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { debounceTime, delay, finalize, map, takeUntil, tap } from 'rxjs';

import { MatSelectInfiniteScrollDirective } from 'src/app/core/directives/mat-select-infinite-scroll.directive';
import { BaseFormKitModule } from '../base/base-form-kit.module';
import { BaseChipListSelectSearchProperties } from './base/base-chip-list-select-search-properties.component';
import { RemoveSelectedItemsPipe } from './remove-selected-items.pipe';

@Component({
  selector: 'chip-list-select-search',
  imports: [
    CommonModule,
    NgxMatSelectSearchModule,
    BaseFormKitModule,
    MatSelectModule,
    MatOptionModule,
    MatChipsModule,
    MatSelectInfiniteScrollDirective,
    RemoveSelectedItemsPipe,
  ],
  templateUrl: './chip-list-select-search.component.html',
  styleUrls: [
    './chip-list-select-search.component.scss',
    '../base/base-form-kit.scss',
  ],
})
export class ChipListSelectSearchComponent
  extends BaseChipListSelectSearchProperties
  implements OnInit
{
  readonly selectElement = viewChild<MatSelect>('matSelect');
  readonly selectAllElement = viewChild<MatOption>('matSelectAll');
  isAddTooltipToMatSelect: boolean = false;
  matSelectPadding: string = '';

  limit = 10;
  offset = 0;
  constructor() {
    super();
  }

  ngOnInit() {
    super.ngOnInit();
  }

  ngAfterViewInit(): void {
    if (!this.placeholder)
      this.placeholder = this.optimalSearch
        ? 'messages.OptimalSearch'
        : 'common.search';
  }

  checkIsOpened(isOpened) {
    if (isOpened == true) {
      this.offset = 0;
      this.getNextItems();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['items'] && this.isFetchData) {
      if (this.items && this.items.length) {
        this.baseItems = Object.assign([], this.items);
        this.setFilteredItems();
        this.offset = 0;
        this.getNextItems();
      } else {
        this.items = [];
        this.filteredItems.next([]);
        this.emitValues(null);
        this.emitItems(null, null);
      }
    }
    if (changes['values'] && this.isUseApiFilter)
      this.selectedItems = Object.assign([], this.values);
  }

  getNextItems() {
    if (!this.hasInfiniteScroll) return;
    const result = this.baseItems.slice(0, this.offset + this.limit);
    this.filteredItems.next(result);
    this.offset += this.limit;
  }

  setFilteredItems() {
    try {
      this.initLoading = false;
      // Default items optimal search for display
      if (this.optimalSearch) this.filteredItems.next(this.items.slice(0, 10));
      // Normal search
      else this.filteredItems.next(this.items.slice(0, this.items.length));

      this.filterControl.valueChanges
        .pipe(
          tap(() => (this.loading = true)),
          takeUntil(this.onDestroy$),
          // Optimal search with delay
          debounceTime(this.optimalSearch ? 200 : 0),
          map((searchText) =>
            !this.isUseApiFilter
              ? (searchText as string).toLowerCase().trim()
              : (searchText as string).trim()
          ),
          map((searchText) => {
            if (this.isUseApiFilter) this.filteredItem.emit(searchText);
            else return this.filterWithoutApi(searchText);
          }),
          delay(this.optimalSearch ? 400 : 0),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe((filteredItems) => {
          this.loading = false;
          if (!this.isUseApiFilter) {
            this.baseItems = Object.assign([], filteredItems);
            this.filteredItems.next(filteredItems);
          }
        });
    } catch (error) {
      console.error('ChipListSelectSearch Error => ', error);
    }
  }

  filterWithoutApi(searchText) {
    if (!this.items) return [];

    if (!searchText) {
      // The optimal search returns the first 10 items
      if (this.optimalSearch) this.items.slice(0, 10);
      // Normal search returns all items
      else this.items.slice(0, this.items.length);
    }
    // filter
    return this.items.filter(
      (item) =>
        item[this.optionTitle]
          .toLowerCase()
          .indexOf(searchText?.toLowerCase()) > -1
    );
  }

  onSelectedItemsRemoved(item) {
    try {
      const itemIndex = this.selectedItems.findIndex(
        (selectedItem) =>
          selectedItem[this.optionValue] == item[this.optionValue]
      );
      const valueIndex = this.values.findIndex(
        (value) => value == item[this.optionValue]
      );

      this.selectedItems.splice(itemIndex, 1);
      this.values.splice(valueIndex, 1);
      this.values = Object.assign([], this.values);
      this.selectedItems = Object.assign([], this.selectedItems);

      this.itemsChange.emit(this.selectedItems);
      this.valuesChange.emit(this.values);
    } catch (error) {
      console.error('ChipListSelectSearch Error => ', error);
    }
  }

  selectAll(event) {
    try {
      if (event?.isUserInput) {
        this.isAllSelected = event.source.selected;
        if (this.isAllSelected)
          this.selectElement().options.forEach((item: MatOption) =>
            item.select()
          );
        else
          this.selectElement().options.forEach((item: MatOption) =>
            item.deselect()
          );
      }
    } catch (error) {
      console.error('ChipListSelectSearch Error => ', error);
    }
  }

  emitItems(item, event: any, option?: MatOption) {
    try {
      if (event?.isUserInput) {
        this.isAllSelected = false;
      }
      if (item == null) {
        this.selectedItems = [];
        this.itemsChange.emit(this.selectedItems);
        return;
      }

      const itemIndex = this.selectedItems.findIndex(
        (selectedItem) =>
          selectedItem[this.optionValue] == item[this.optionValue]
      );

      if (!option.selected && itemIndex != -1 && !this.isUseApiFilter)
        this.selectedItems.splice(itemIndex, 1);
      else if (option.selected && itemIndex == -1) {
        if (this.maxTotalCount) {
          if (this.selectedItems.length < this.maxTotalCount)
            this.selectedItems.push(item);
        } else this.selectedItems.push(item);
      }

      // if (
      //   this.selectedItems.length > 0 &&
      //   this.items.length > 0 &&
      //   this.selectedItems.length == this.items.length
      // ) {
      //   this.selectElement().options.forEach((item: MatOption) => {
      //     item.select();
      //   });
      // } else this.selectAllElement()?.deselect();

      if (this.selectedItems.length != 0) this.matSelectPadding = 'fk-pb-1';
      else this.matSelectPadding = '';

      if (!event?.isUserInput) {
        if (this.isAllSelected) {
          if (this.selectedItems.length == this.items.length) {
            this.itemsChange.emit(this.selectedItems);
          }
        } else if (this.selectedItems.length == 0)
          this.itemsChange.emit(this.selectedItems);
      } else {
        this.itemsChange.emit(this.selectedItems);
      }
    } catch (error) {
      console.error('ChipListSelectSearch Error => ', error);
    }
  }

  emitValues(values) {
    try {
      values = values?.filter((item) => item || item === 0);
      this.valuesChange.emit(values);

      if (this.isDeleteAfterEmit) {
        const timeOut = setTimeout(() => {
          this.clear();
          clearTimeout(timeOut);
        }, 100);
      }
    } catch (error) {
      console.error('ChipListSelectSearch Error => ', error);
    }
  }

  //#endregion

  clear() {
    this.values = [];
    this.selectedItems = [];
    this.itemsChange.emit([]);
    this.valuesChange.emit([]);
  }

  unSubs(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  ngOnDestroy(): void {
    this.unSubs();
  }
} 
